import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { AsyncEntity } from '../../global';
import createAsyncThunkFromFetch from '../../store/middlewares/createAsyncThunkFromFetch';
import {
  createUser as createUserAPI,
  deleteUser as deleteUserAPI,
  updateUser as updateUserAPI,
  getUser,
  getUsers,
} from './users.api';
import { User } from './users.types';

const name = 'users';

const usersEntityAdapter = createEntityAdapter<AsyncEntity<User>>({
  selectId: user => user.id,
});

export const usersSelectors = usersEntityAdapter.getSelectors();

export const fetchUsers = createAsyncThunkFromFetch(`${name}/fetchUsers`, getUsers);
export const createUser = createAsyncThunkFromFetch(`${name}/createUser`, createUserAPI);
export const fetchUser = createAsyncThunkFromFetch(`${name}/fetchUser`, getUser);
export const updateUser = createAsyncThunkFromFetch(`${name}/updateUser`, updateUserAPI);
export const deleteUser = createAsyncThunkFromFetch(`${name}/deleteUser`, deleteUserAPI);

const usersSlice = createSlice({
  name,
  initialState: usersEntityAdapter.getInitialState({
    isPending: false,
    hasError: false,
  }),
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchUsers.pending, state => {
      state.isPending = true;
      state.hasError = false;
    });
    builder.addCase(fetchUsers.fulfilled, (state, { payload }) => {
      usersEntityAdapter.upsertMany(state, payload);
      state.isPending = false;
      state.hasError = false;
    });
    builder.addCase(fetchUsers.rejected, state => {
      state.isPending = false;
      state.hasError = true;
    });
    builder.addCase(deleteUser.pending, (state, { meta }) => {
      usersEntityAdapter.updateOne(state, {
        id: meta.arg.userId,
        changes: { isPending: true },
      });
    });
    builder.addCase(deleteUser.fulfilled, (state, { meta }) => {
      usersEntityAdapter.removeOne(state, meta.arg.userId);
    });
    builder.addCase(deleteUser.rejected, (state, { meta }) => {
      usersEntityAdapter.updateOne(state, {
        id: meta.arg.userId,
        changes: { isPending: false, hasError: true },
      });
    });
    builder.addCase(createUser.fulfilled, (state, { payload }) => {
      usersEntityAdapter.upsertOne(state, payload);
    });
    builder.addCase(fetchUser.fulfilled, (state, { payload }) => {
      usersEntityAdapter.upsertOne(state, payload);
    });
    builder.addCase(updateUser.fulfilled, (state, { payload }) => {
      usersEntityAdapter.updateOne(state, { id: payload.id, changes: payload });
    });
  },
});

const { reducer } = usersSlice;

export default reducer;
